/**
 * Lightweight first-name gender guess for admin stats (Arabic + common Latin).
 * Returns 'male' | 'female' | 'unknown'.
 */

const MALE = new Set([
  // Arabic / Egyptian (Latin)
  'ahmed', 'ahmad', 'mohamed', 'mohammed', 'muhammad', 'mohammad', 'mostafa', 'mustafa',
  'mahmoud', 'mahmud', 'omar', 'osama', 'youssef', 'yousef', 'yusuf', 'yasser', 'yasir',
  'karim', 'kareem', 'hassan', 'hasan', 'hussein', 'hussain', 'ali', 'amr', 'omar',
  'ibrahim', 'ismail', 'tarek', 'tareq', 'tarik', 'tamer', 'sherif', 'sharif', 'sameh',
  'samir', 'saeed', 'said', 'khaled', 'khalid', 'walid', 'waleed', 'wael', 'nabil',
  'nasser', 'nasr', 'hany', 'hani', 'hesham', 'hisham', 'ashraf', 'adel', 'adham',
  'ayman', 'basel', 'bassem', 'basim', 'bilal', 'fady', 'fadi', 'fouad', 'fawzy',
  'gamal', 'jamal', 'gaber', 'haitham', 'haytham', 'hosam', 'hossam', 'magdy', 'magdi',
  'marwan', 'mina', 'nagy', 'nagi', 'ramy', 'rami', 'raouf', 'reda', 'riyad', 'riyadh',
  'seif', 'shady', 'shadi', 'ziad', 'zyad', 'zyad', 'abdallah', 'abdullah', 'abdelrahman',
  'abdelrahman', 'abdelrahman', 'abdo', 'abdou', 'ehab', 'ihab', 'essam', 'issam',
  'emad', 'imad', 'farid', 'faisal', 'hamza', 'hazem', 'hazim', 'kamel', 'loay', 'louay',
  'malek', 'malik', 'mansour', 'maged', 'majed', 'nader', 'osman', 'othman', 'rafik',
  'rafiq', 'saleh', 'salah', 'samy', 'sami', 'sherief', 'sobhy', 'sobhi', 'taha',
  'wassim', 'wasim', 'yahia', 'yahya', 'zakaria', 'zaki', 'zayn', 'zain',
  'abd', 'abdul', 'abdel',
  // Common English
  'john', 'james', 'michael', 'david', 'daniel', 'mark', 'paul', 'peter', 'thomas',
  'andrew', 'chris', 'christopher', 'alex', 'alexander', 'adam', 'ryan', 'kevin',
  'brian', 'joseph', 'steven', 'stephen', 'robert', 'william', 'richard', 'george',
  'anthony', 'matthew', 'joshua', 'jack', 'lucas', 'leo', 'noah', 'liam', 'ethan',
  // Arabic script
  'أحمد', 'محمد', 'محمود', 'عمر', 'يوسف', 'ياسر', 'كريم', 'حسن', 'حسين', 'علي',
  'إبراهيم', 'ابراهيم', 'إسماعيل', 'اسماعيل', 'طارق', 'تامر', 'شريف', 'خالد',
  'وليد', 'وائل', 'هاني', 'هشام', 'أشرف', 'عادل', 'أيمن', 'فادي', 'جمال', 'مروان',
  'رامي', 'زياد', 'عبدالله', 'إيهاب', 'عماد', 'حمزة', 'حازم', 'مالك', 'نادر', 'سامي',
]);

const FEMALE = new Set([
  // Arabic / Egyptian (Latin)
  'sara', 'sarah', 'saraa', 'salma', 'somaia', 'somaya', 'somia', 'nour', 'noor',
  'noura', 'nora', 'norah', 'nada', 'nadine', 'nadia', 'nady', 'mai', 'may', 'maha',
  'mariam', 'maryam', 'miriam', 'maria', 'marie', 'mona', 'menna', 'mena', 'malak',
  'farah', 'fatma', 'fatima', 'fatimah', 'faten', 'hala', 'hager', 'hagar', 'heba',
  'hiba', 'hoda', 'huda', 'hana', 'hannah', 'hanan', 'iman', 'eman', 'amira', 'ameera',
  'aya', 'ayah', 'ayat', 'asmaa', 'asma', 'asmaa', 'basma', 'dina', 'deena', 'dalal',
  'doaa', 'doa', 'duaa', 'esraa', 'esra', 'israa', 'isra', 'ghada', 'ghada', 'habiba',
  'jasmin', 'jasmine', 'yasmin', 'yasmeen', 'yasmine', 'laila', 'layla', 'leila',
  'lamia', 'lamya', 'lina', 'lena', 'lobna', 'lubna', 'magda', 'manal', 'marwa',
  'mayada', 'mervat', 'mirna', 'myrna', 'nagla', 'naglaa', 'nancy', 'nehal', 'nihal',
  'nermeen', 'nermin', 'omnia', 'omneya', 'omnya', 'rana', 'rania', 'ranya', 'rawan',
  'reem', 'rim', 'riham', 'roqaya', 'ruqaya', 'sabah', 'sahar', 'samah', 'samia',
  'samiha', 'sandy', 'shaimaa', 'shaima', 'shereen', 'sherine', 'shimaa', 'shima',
  'souad', 'suad', 'susan', 'suzan', 'tahany', 'wafaa', 'wafa', 'walaa', 'wala',
  'yara', 'youmna', 'yumna', 'zeinab', 'zainab', 'zinab', 'zahra', 'zahraa',
  'amani', 'basant', 'basmala', 'carmen', 'christina', 'dalia', 'diana', 'donna',
  'enjy', 'engy', 'inji', 'farida', 'gilan', 'habiba', 'hend', 'hind', 'jana',
  'judy', 'julie', 'karen', 'laura', 'logan', 'lorine', 'marina', 'melissa', 'michelle',
  'natalie', 'nicole', 'passant', 'pascale', 'rachel', 'rita', 'sandra', 'sophia',
  'stephanie', 'tracy', 'veronica', 'vivian',
  // Common English
  'emma', 'olivia', 'ava', 'sophia', 'isabella', 'mia', 'charlotte', 'amelia',
  'emily', 'elizabeth', 'sofia', 'evelyn', 'abigail', 'ella', 'scarlett', 'grace',
  'chloe', 'victoria', 'riley', 'aria', 'lily', 'lucy', 'anna', 'anne', 'jennifer',
  'jessica', 'ashley', 'amanda', 'stephanie', 'melissa', 'nicole', 'michelle',
  'kimberly', 'amy', 'angela', 'brenda', 'emma', 'helen', 'deborah', 'rachel',
  'carolyn', 'janet', 'catherine', 'maria', 'heather', 'diane', 'julie', 'joyce',
  // Arabic script
  'سارة', 'سلمى', 'نور', 'نورا', 'نادية', 'مريم', 'منى', 'ملكة', 'فرح', 'فاطمة',
  'هالة', 'هبة', 'هدى', 'إيمان', 'ايمان', 'أميرة', 'اميرة', 'آية', 'أسماء', 'اسماء',
  'دينا', 'دعاء', 'إسراء', 'اسراء', 'غادة', 'ياسمين', 'ليلى', 'لينا', 'منال', 'مروة',
  'رانيا', 'ريم', 'سحر', 'شيماء', 'شيرين', 'وفاء', 'يارا', 'زينب', 'زهراء', 'هند',
]);

function normalizeToken(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670]/g, '') // Arabic diacritics
    .replace(/[^a-z\u0600-\u06FF'-]/g, '');
}

function firstNameFromFull(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '';
  // Skip honorifics / compound starters
  const skip = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'eng', 'م', 'أ', 'د']);
  for (const part of parts) {
    const n = normalizeToken(part);
    if (!n || skip.has(n)) continue;
    // "Abd El ..." / "Abdel..." treated as male prefix often with next token
    if (n === 'abd' || n === 'abdel' || n === 'abdul' || n.startsWith('abdel') || n.startsWith('abdul')) {
      return 'male';
    }
    return n;
  }
  return normalizeToken(parts[0]);
}

export function guessGenderFromName(fullName) {
  const first = firstNameFromFull(fullName);
  if (!first) return 'unknown';
  if (MALE.has(first)) return 'male';
  if (FEMALE.has(first)) return 'female';

  // Soft Arabic/Latin heuristics when not in lists
  if (/ة$|ى$|اء$/.test(first)) return 'female';
  if (/a$|ah$|ia$|ya$|een$|ine$/.test(first) && first.length > 3) return 'female';

  return 'unknown';
}

export function countGendersByName(users) {
  const counts = { male: 0, female: 0, unknown: 0 };
  (users || []).forEach((u) => {
    const g = guessGenderFromName(u?.name);
    counts[g] += 1;
  });
  return counts;
}
